// Nine Lives '84 - room views.
//
// The scenery and moving parts of whichever room Tabs is in: the shell,
// the broom and pawprints (shared), and one "extra" per room kind for its
// furniture and creatures. Built when a room starts and thrown away when
// it ends.

import * as THREE from 'three';
import { SPIDER_H, WORLD_H } from '../sim/constants';
import type { Room, Pawprint } from '../sim/room';
import { BirdcageRoom } from '../sim/rooms/birdcage';
import { BookshelfRoom, type Fern } from '../sim/rooms/bookshelf';
import { CheeseRoom } from '../sim/rooms/cheese';
import { DogsRoom, type SleepingDog } from '../sim/rooms/dogs';
import { FeliciaRoom, type Arrow, type Gift } from '../sim/rooms/felicia';
import { FishbowlRoom, type Eel, type Fish } from '../sim/rooms/fishbowl';
import type { Box } from '../sim/types';
import {
  buildArrow,
  buildBird,
  buildCat,
  buildCupid,
  buildFelicia,
  buildFeliciaShell,
  buildGift,
  buildHeart,
  type CatModel,
  type CupidModel,
  type HeartModel,
  buildBookcase,
  buildBowl,
  buildBroom,
  buildBrokenCage,
  buildCage,
  buildCarton,
  buildCheese,
  buildDog,
  buildEel,
  buildFern,
  buildFish,
  buildFishbowl,
  buildPawprint,
  buildPeekingMouse,
  buildRoomShell,
  buildSpider,
  buildTable,
  buildTank,
  CHEESE_FACE_Z,
  type BirdModel,
  type BowlModel,
  type BroomModel,
  type DogModel,
  type EelModel,
  type SpiderModel,
} from './models';
import { shadowify } from './materials';
import { EntityView } from './views';

/** What a room kind adds on top of the shared shell, broom and prints. */
interface RoomExtra {
  readonly group: THREE.Group;
  commit(room: Room): void;
  present(alpha: number, time: number): void;
  /** True while Tabs should not be drawn. */
  readonly hidesCat: boolean;
  /** True while the room shell and broom should not be drawn (the tank). */
  readonly hidesRoom: boolean;
  /** Tabs is swimming (paddle pose). */
  readonly swimming: boolean;
  /** Tabs is short of air (pink tint). */
  readonly gasping: boolean;
}

export class RoomViews {
  readonly group = new THREE.Group();
  private readonly shell: THREE.Group;
  private readonly broom: BroomModel;
  private readonly broomView: EntityView;
  private broomTarget: 'print' | 'cat' | 'none' = 'none';
  private readonly prints = new Map<Pawprint, THREE.Object3D>();
  private readonly extra: RoomExtra;

  constructor(
    private readonly scene: THREE.Scene,
    room: Room,
  ) {
    if (room instanceof CheeseRoom) {
      this.shell = buildRoomShell(room, 0xc9b98a, 0x6a5a48);
      this.extra = new CheeseExtra(room);
    } else if (room instanceof BookshelfRoom) {
      this.shell = buildRoomShell(room, 0x6f7a8a, 0x5a4a3a);
      this.extra = new BookshelfExtra(room);
    } else if (room instanceof BirdcageRoom) {
      this.shell = buildRoomShell(room, 0x9a7a8a, 0x5a4a3a);
      this.extra = new BirdcageExtra(room);
    } else if (room instanceof DogsRoom) {
      this.shell = buildRoomShell(room, 0x7a8a6a, 0x6a5a48);
      this.extra = new DogsExtra(room);
    } else if (room instanceof FeliciaRoom) {
      this.shell = buildFeliciaShell(room.ledge);
      this.extra = new FeliciaExtra(room);
    } else {
      this.shell = buildRoomShell(room, 0x6a8a9a, 0x5a4a3a);
      this.extra = new FishbowlExtra(room as FishbowlRoom);
    }
    this.broom = buildBroom();
    this.broom.root.visible = !(room instanceof FeliciaRoom);
    this.broomView = new EntityView(this.broom.root);
    this.group.add(this.shell, this.extra.group, this.broom.root);
    scene.add(this.group);
    this.commit(room);
    this.broomView.snap();
  }

  get hidesCat(): boolean {
    return this.extra.hidesCat;
  }

  get swimming(): boolean {
    return this.extra.swimming;
  }

  get gasping(): boolean {
    return this.extra.gasping;
  }

  dispose(): void {
    this.scene.remove(this.group);
  }

  commit(room: Room): void {
    this.broomView.commit(room.broom, 1);
    this.broomTarget = room.broom.target;

    const seen = new Set<Pawprint>();
    for (const p of room.prints) {
      seen.add(p);
      if (!this.prints.has(p)) {
        const m = buildPawprint();
        m.position.set(p.x, 2.2, 2);
        this.prints.set(p, m);
        this.group.add(m);
      }
    }
    for (const [p, m] of this.prints) {
      if (!seen.has(p)) {
        this.group.remove(m);
        this.prints.delete(p);
      }
    }
    this.extra.commit(room);
    const show = !this.extra.hidesRoom;
    this.shell.visible = show;
    this.broom.root.visible = show && !(room instanceof FeliciaRoom);
    for (const m of this.prints.values()) m.visible = show;
  }

  present(alpha: number, time: number): void {
    const bp = this.broomView.pose(alpha);
    // The broom flies in front of the furniture so it never vanishes into it.
    this.broom.root.position.set(bp.x, bp.y, 12);
    // Sweeping wag when over a print; a lean when chasing.
    this.broom.root.rotation.z =
      this.broomTarget === 'print' ? Math.sin(time * 14) * 0.35 : Math.sin(time * 3) * 0.12;
    this.extra.present(alpha, time);
  }
}

/** Keep one EntityView per live entity of a list, creating and dropping as needed. */
function pool<T extends Box>(
  parent: THREE.Object3D,
  views: Map<T, EntityView>,
  live: readonly T[],
  build: () => THREE.Object3D,
  facing: (t: T) => number,
): void {
  const seen = new Set<T>();
  for (const e of live) {
    seen.add(e);
    let v = views.get(e);
    if (!v) {
      v = new EntityView(build());
      shadowify(v.root);
      v.commit(e, facing(e));
      v.snap();
      views.set(e, v);
      parent.add(v.root);
    } else {
      v.commit(e, facing(e));
    }
  }
  for (const [e, v] of views) {
    if (!seen.has(e)) {
      parent.remove(v.root);
      views.delete(e);
    }
  }
}

function place(v: EntityView, alpha: number, z = 0): void {
  const p = v.pose(alpha);
  v.root.position.set(p.x, p.y, z);
}

/** Pose a dog model: running, lying asleep, or bristling. */
function poseDog(
  dog: DogModel,
  facing: number,
  time: number,
  mode: 'run' | 'still' | 'asleep' | 'growl' | 'bite',
): void {
  dog.body.scale.x = facing;
  const stride = mode === 'run' ? Math.sin(time * 16) * 0.7 : 0;
  dog.legs.forEach((leg, i) => {
    if (mode === 'asleep' || mode === 'growl') {
      leg.rotation.z = i < 2 ? 1.3 : -1.3;
      leg.position.y = 1.2;
    } else {
      leg.rotation.z = stride * (i % 2 ? 1 : -1);
      leg.position.y = 2;
    }
  });
  if (mode === 'asleep' || mode === 'growl') {
    dog.body.position.y = -3 + Math.sin(time * 2) * 0.4;
    dog.body.rotation.z = mode === 'growl' ? Math.sin(time * 40) * 0.05 : 0;
  } else {
    dog.body.position.y = 0;
    dog.body.rotation.z = 0;
  }
  dog.jaw.position.y = mode === 'bite' || mode === 'growl' ? 4.6 + Math.abs(Math.sin(time * 30)) * 1.5 : 5.6;
}

class CheeseExtra implements RoomExtra {
  readonly group = new THREE.Group();
  private readonly peekers: THREE.Object3D[] = [];
  hidesCat = false;
  readonly hidesRoom = false;
  readonly swimming = false;
  readonly gasping = false;

  constructor(room: CheeseRoom) {
    this.group.add(buildCheese(room));
    for (const h of room.holes) {
      const m = buildPeekingMouse();
      m.position.set(h.x, h.y, CHEESE_FACE_Z);
      m.visible = false;
      this.peekers.push(m);
      this.group.add(m);
    }
  }

  commit(room: Room): void {
    const r = room as CheeseRoom;
    r.holes.forEach((h, i) => {
      const m = this.peekers[i];
      m.visible = h.mouse > 0;
      // Pops out, then eases back in over the last moments.
      const out = Math.min(1, h.mouse / 20);
      m.position.z = CHEESE_FACE_Z - 4 + out * 4;
    });
    this.hidesCat = r.crawling > 0;
  }

  present(): void {}
}

class BookshelfExtra implements RoomExtra {
  readonly group = new THREE.Group();
  private readonly ferns = new Map<Fern, EntityView>();
  private readonly spider: SpiderModel;
  private readonly spiderView: EntityView;
  private spiderState: 'lurk' | 'drop' | 'rise' = 'lurk';
  readonly hidesCat = false;
  readonly hidesRoom = false;
  readonly swimming = false;
  readonly gasping = false;

  constructor(room: BookshelfRoom) {
    this.group.add(buildBookcase(room));
    for (const f of room.ferns) {
      const v = new EntityView(buildFern());
      v.commit(f, 1);
      v.snap();
      this.ferns.set(f, v);
      this.group.add(v.root);
    }
    this.spider = buildSpider();
    this.spiderView = new EntityView(this.spider.root);
    this.spiderView.commit(room.spider, 1);
    this.spiderView.snap();
    this.group.add(this.spider.root);
  }

  commit(room: Room): void {
    const r = room as BookshelfRoom;
    for (const f of r.ferns) {
      const v = this.ferns.get(f)!;
      v.commit(f, 1);
      v.root.visible = f.state !== 'down';
    }
    this.spiderView.commit(r.spider, 1);
    this.spiderState = r.spider.state;
  }

  present(alpha: number, time: number): void {
    for (const v of this.ferns.values()) {
      place(v, alpha);
      v.root.rotation.z = v.cur.y > 0 && v.cur.y !== v.prev.y ? time * 5 : 0;
    }
    const p = this.spiderView.pose(alpha);
    this.spider.root.position.set(p.x, p.y, 4);
    // The thread reaches from the body up to the ceiling.
    this.spider.thread.scale.y = Math.max(1, WORLD_H - p.y - SPIDER_H);
    const wiggle = this.spiderState === 'lurk' ? 0.15 : 0.5;
    this.spider.legs.forEach((leg, i) => {
      leg.rotation.z = ((i % 4) - 1.5) * 0.3 + Math.sin(time * 12 + i) * wiggle;
    });
  }
}

class BirdcageExtra implements RoomExtra {
  readonly group = new THREE.Group();
  private readonly cage: THREE.Group;
  private readonly broken: THREE.Group;
  private readonly cageView: EntityView;
  private cageState: 'up' | 'falling' | 'broken' = 'up';
  private readonly bird: BirdModel;
  private readonly birdView: EntityView;
  private birdLoose = false;
  private readonly dog: DogModel;
  private readonly dogView: EntityView;
  private dogMode: 'run' | 'bite' = 'run';
  readonly hidesCat = false;
  readonly hidesRoom = false;
  readonly swimming = false;
  readonly gasping = false;

  constructor(room: BirdcageRoom) {
    this.group.add(buildTable(room.table));
    this.cage = buildCage();
    this.broken = buildBrokenCage();
    this.broken.visible = false;
    this.group.add(this.cage, this.broken);
    this.cageView = new EntityView(this.cage);
    this.cageView.commit(room.cage, 1);
    this.cageView.snap();
    this.bird = buildBird();
    this.bird.root.visible = false;
    this.birdView = new EntityView(this.bird.root);
    this.group.add(this.bird.root);
    this.dog = buildDog();
    this.dogView = new EntityView(this.dog.root);
    this.dogView.commit(room.dog, room.dog.dir);
    this.dogView.snap();
    this.group.add(this.dog.root);
  }

  commit(room: Room): void {
    const r = room as BirdcageRoom;
    this.cageView.commit(r.cage, 1);
    this.cageState = r.cage.state;
    if (r.bird) {
      if (!this.birdLoose) {
        this.birdView.commit(r.bird, 1);
        this.birdView.snap();
        this.birdLoose = true;
      }
      this.birdView.commit(r.bird, r.bird.vx >= 0 ? 1 : -1);
      this.bird.root.visible = !r.birdCaught;
    }
    this.dogView.commit(r.dog, r.dog.dir);
    this.dogMode = r.fatal === 'dog' ? 'bite' : 'run';
  }

  present(alpha: number, time: number): void {
    const p = this.cageView.pose(alpha);
    const broken = this.cageState === 'broken';
    this.cage.visible = !broken;
    this.broken.visible = broken;
    this.cage.position.set(p.x, p.y, 0);
    this.cage.rotation.z = this.cageState === 'falling' ? time * 4 : 0;
    this.broken.position.set(p.x, p.y, 0);
    if (this.birdLoose) {
      const b = this.birdView.pose(alpha);
      this.bird.root.position.set(b.x, b.y, 6);
      this.bird.root.scale.x = b.facing;
      const flap = Math.sin(time * 22) * 0.8;
      this.bird.wings[0].rotation.x = -flap;
      this.bird.wings[1].rotation.x = flap;
    }
    const d = this.dogView.pose(alpha);
    this.dog.root.position.set(d.x, d.y, 2);
    poseDog(this.dog, d.facing, time, this.dogMode);
  }
}

class DogsExtra implements RoomExtra {
  readonly group = new THREE.Group();
  private readonly bowls: BowlModel[] = [];
  private readonly dogs: DogModel[] = [];
  private readonly dogViews: EntityView[] = [];
  private states: SleepingDog['state'][] = [];
  /** A wake meter floating over each dog: fills as Tabs lingers near. */
  private readonly meters: THREE.Mesh[] = [];
  private readonly meterFrames: THREE.Mesh[] = [];
  private meterLevels: number[] = [];
  private readonly carton: THREE.Group;
  private readonly cartonView: EntityView;
  private pouring = false;
  readonly hidesCat = false;
  readonly hidesRoom = false;
  readonly swimming = false;
  readonly gasping = false;

  constructor(room: DogsRoom) {
    for (const b of room.bowls) {
      const m = buildBowl();
      m.root.position.set(b.x, b.y, 0);
      this.bowls.push(m);
      this.group.add(m.root);
    }
    for (const d of room.dogs) {
      const m = buildDog();
      const v = new EntityView(m.root);
      v.commit(d, d.dir);
      v.snap();
      this.dogs.push(m);
      this.dogViews.push(v);
      this.group.add(m.root);
      const frame = new THREE.Mesh(
        new THREE.BoxGeometry(18, 3, 0.6),
        new THREE.MeshBasicMaterial({ color: 0x14181c, transparent: true, opacity: 0.7 }),
      );
      const fill = new THREE.Mesh(new THREE.BoxGeometry(16, 1.8, 0.8), new THREE.MeshBasicMaterial({ color: 0x2ec4b6, toneMapped: false }));
      fill.geometry.translate(8, 0, 0); // grows from its left end
      this.meterFrames.push(frame);
      this.meters.push(fill);
      this.group.add(frame, fill);
    }
    // A rug under the dogs.
    const rug = new THREE.Mesh(new THREE.BoxGeometry(300, 0.6, 40), new THREE.MeshToonMaterial({ color: 0x8a3a3a }));
    rug.position.set(160, 0.3, -6);
    this.group.add(rug);
    this.carton = buildCarton();
    this.cartonView = new EntityView(this.carton);
    this.cartonView.commit(room.carton, 1);
    this.cartonView.snap();
    this.group.add(this.carton);
  }

  commit(room: Room): void {
    const r = room as DogsRoom;
    r.bowls.forEach((b, i) => {
      const milk = this.bowls[i].milk;
      milk.visible = b.level > 0;
      milk.scale.set(0.3 + b.level * 0.7, 1, 0.3 + b.level * 0.7);
      milk.position.y = 1.5 + b.level * (6 - 2.1);
    });
    r.dogs.forEach((d, i) => this.dogViews[i].commit(d, d.dir));
    this.meterLevels = r.dogs.map((d) => (d.state === 'awake' || d.state === 'returning' ? 1 : d.meter));
    this.states = r.dogs.map((d) => (r.fatal === 'dog' && d.state === 'awake' ? 'bite' : d.state) as SleepingDog['state']);
    this.cartonView.commit(r.carton, 1);
    this.pouring = r.carton.state === 'pour';
  }

  present(alpha: number, time: number): void {
    this.dogs.forEach((dog, i) => {
      const p = this.dogViews[i].pose(alpha);
      dog.root.position.set(p.x, p.y, 2);
      const s = this.states[i] as SleepingDog['state'] | 'bite';
      const mode =
        s === 'asleep' ? 'asleep' : s === 'growl' ? 'growl' : s === 'bite' ? 'bite' : 'run';
      poseDog(dog, p.facing, time, mode);
      // The wake meter above the dog: teal while sleepy, red once it growls.
      const level = this.meterLevels[i] ?? 0;
      const frame = this.meterFrames[i];
      const fill = this.meters[i];
      const shown = level > 0.02;
      frame.visible = shown;
      fill.visible = shown;
      frame.position.set(p.x + 12, p.y + 18, 6);
      fill.position.set(p.x + 4, p.y + 18, 6.5);
      fill.scale.x = Math.max(0.01, level);
      (fill.material as THREE.MeshBasicMaterial).color.set(
        s === 'growl' || s === 'awake' || s === 'bite' ? 0xd94f4a : 0x2ec4b6,
      );
    });
    const c = this.cartonView.pose(alpha);
    if (this.pouring) {
      // Tipped over the bowl to its right, spout down.
      this.carton.position.set(c.x + 12, c.y + 4, 0);
      this.carton.rotation.z = -1.9;
    } else {
      this.carton.position.set(c.x, c.y + Math.sin(time * 2) * 3, 0);
      this.carton.rotation.z = Math.sin(time * 1.5) * 0.15;
    }
  }
}

const BROTHER_COLOURS: [number, number][] = [
  [0xd08a3a, 0xf0c090],
  [0x8a5a3a, 0xc9a27a],
  [0x3a3f4a, 0x8a8f9a],
  [0xb04a3a, 0xe0a090],
  [0x6a6a70, 0xb0b0b8],
  [0xc9a24a, 0xf0e0b0],
];

class FeliciaExtra implements RoomExtra {
  readonly group = new THREE.Group();
  private readonly hearts: HeartModel[] = [];
  private readonly felicia: CatModel;
  private readonly brothers: CatModel[] = [];
  private readonly brotherViews: EntityView[] = [];
  private bribed: boolean[] = [];
  private readonly cupids: CupidModel[] = [];
  private readonly arrows = new Map<Arrow, EntityView>();
  private readonly gifts = new Map<Gift, EntityView>();
  readonly hidesCat = false;
  readonly hidesRoom = false;
  readonly swimming = false;
  readonly gasping = false;

  constructor(room: FeliciaRoom) {
    for (const h of room.hearts) {
      const m = buildHeart();
      m.root.position.set(h.x, h.y, 0);
      this.hearts.push(m);
      this.group.add(m.root);
    }
    this.felicia = buildFelicia();
    this.felicia.root.position.set(room.felicia.x, room.felicia.y, 0);
    this.felicia.body.scale.x = -1;
    this.group.add(this.felicia.root);
    room.brothers.forEach((b, i) => {
      const [fur, light] = BROTHER_COLOURS[i % BROTHER_COLOURS.length];
      const m = buildCat(fur, light);
      const v = new EntityView(m.root);
      v.commit(b, b.facing);
      v.snap();
      this.brothers.push(m);
      this.brotherViews.push(v);
      this.group.add(m.root);
    });
    for (const c of room.cupids) {
      const m = buildCupid();
      m.root.position.set(c.x, c.y, 2);
      m.root.scale.x = c.side;
      if (c.side < 0) m.root.position.x += c.w;
      this.cupids.push(m);
      this.group.add(m.root);
    }
  }

  commit(room: Room): void {
    const r = room as FeliciaRoom;
    r.hearts.forEach((h, i) => {
      this.hearts[i].whole.visible = h.whole;
      this.hearts[i].broken.visible = !h.whole;
    });
    r.brothers.forEach((b, i) => this.brotherViews[i].commit(b, b.facing));
    this.bribed = r.brothers.map((b) => b.bribed > 0);
    pool(this.group, this.arrows, r.arrows, buildArrow, (a) => (a.vx >= 0 ? 1 : -1));
    pool(this.group, this.gifts, r.gifts, buildGift, () => 1);
  }

  present(alpha: number, time: number): void {
    this.brothers.forEach((m, i) => {
      const p = this.brotherViews[i].pose(alpha);
      m.root.position.set(p.x, p.y, 1);
      m.body.scale.x = p.facing;
      const moving = this.brotherViews[i].cur.x !== this.brotherViews[i].prev.x;
      const stride = moving && !this.bribed[i] ? Math.sin(time * 16 + i) * 0.6 : 0;
      m.legs.forEach((leg, j) => {
        leg.rotation.z = stride * (j % 2 ? 1 : -1);
      });
      // A bribed brother sits back, purring over his gift.
      m.body.rotation.z = this.bribed[i] ? -0.35 : 0;
      m.tail.rotation.z = -0.5 - Math.sin(time * (this.bribed[i] ? 2 : 6) + i) * 0.3;
    });
    this.felicia.tail.rotation.z = -0.6 - Math.sin(time * 2.5) * 0.3;
    this.felicia.body.position.y = Math.abs(Math.sin(time * 1.5)) * 0.8;
    this.cupids.forEach((c, i) => {
      c.root.position.y = 172 + Math.sin(time * 2 + i * 2) * 3;
      const flap = Math.sin(time * 14 + i) * 0.5;
      c.wings[0].rotation.y = -flap;
      c.wings[1].rotation.y = flap;
    });
    for (const v of this.arrows.values()) {
      const p = v.pose(alpha);
      v.root.position.set(p.x, p.y, 8);
      const a = [...this.arrows.entries()].find(([, vv]) => vv === v)?.[0];
      v.root.rotation.z = a ? Math.atan2(a.vy, a.vx) : 0;
    }
    for (const v of this.gifts.values()) {
      place(v, alpha, 2);
      v.root.position.y += Math.abs(Math.sin(time * 3)) * 1.5;
    }
  }
}

class FishbowlExtra implements RoomExtra {
  readonly group = new THREE.Group();
  private readonly roomGroup = new THREE.Group();
  private readonly tankGroup = new THREE.Group();
  private readonly fish = new Map<Fish, EntityView>();
  private readonly eels = new Map<Eel, EntityView>();
  private readonly eelModels = new Map<Eel, EelModel>();
  private inTank = false;
  readonly hidesCat = false;
  gasping = false;

  constructor(room: FishbowlRoom) {
    this.roomGroup.add(buildTable(room.table), buildFishbowl(room.bowl));
    this.tankGroup.add(buildTank());
    this.tankGroup.visible = false;
    this.group.add(this.roomGroup, this.tankGroup);
  }

  get hidesRoom(): boolean {
    return this.inTank;
  }

  get swimming(): boolean {
    return this.inTank;
  }

  commit(room: Room): void {
    const r = room as FishbowlRoom;
    this.inTank = r.phase === 'tank';
    this.roomGroup.visible = !this.inTank;
    this.tankGroup.visible = this.inTank;
    this.gasping = this.inTank && r.airFraction < 0.3;
    const colours = [0xff8a3a, 0xffd166, 0x5ec4e8, 0xd94f8a, 0x9ad04a];
    let n = 0;
    pool(this.tankGroup, this.fish, r.fish, () => buildFish(colours[n++ % colours.length]), (f) => (f.vx >= 0 ? 1 : -1));
    pool(
      this.tankGroup,
      this.eels,
      r.eels,
      () => {
        const m = buildEel();
        return m.root;
      },
      (e) => (e.vx >= 0 ? 1 : -1),
    );
    for (const [e, v] of this.eels) {
      if (!this.eelModels.has(e)) {
        // Recover the model's segments from the built root.
        const segments = v.root.children.filter((c) => (c as THREE.Mesh).isMesh) as THREE.Mesh[];
        this.eelModels.set(e, { root: v.root as THREE.Group, segments });
      }
    }
  }

  present(alpha: number, time: number): void {
    if (!this.inTank) return;
    for (const v of this.fish.values()) {
      const p = v.pose(alpha);
      v.root.position.set(p.x, p.y, 0);
      v.root.scale.x = p.facing;
      v.root.rotation.z = Math.sin(time * 6) * 0.1;
    }
    for (const [e, v] of this.eels) {
      const p = v.pose(alpha);
      v.root.position.set(p.x, p.y, 0);
      v.root.scale.x = p.facing;
      const m = this.eelModels.get(e);
      m?.segments.forEach((s, i) => {
        s.position.y = EEL_HALF + Math.sin(time * 9 + i * 1.2) * 1.6;
      });
    }
  }
}

const EEL_HALF = 3;
